import {
    IMachineCenter,
    IFailureType,
    IMQTTBrokerSettings,
    IDefaultUptimeValues,
    IStore,
    IEventsEditionsPending,
    IManualQualityPending,
} from "./HMICachedDataContextInterfaces";
import { eventReportData } from "./HMICachedDataContext";
import {
    getEditionsPendingHMIStore,
    getEventReportEditData,
    getEventReportEditing,
    getLocalStorageItem,
    getManualQualityPendingHMIStore,
    resetLocalStorage,
    setEventReportEditData,
    setLocalStorageItem,
} from "utilities/storageUtilities";
import { IDetails } from "pages/HMI/HMIDashboard/components/Details/DetailsAPIInterfaces";
import { IEventWithHours, ILastTwoShiftsEvents } from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { ApolloError } from "@apollo/client";
import {
    FAILURE_TYPES_CODE_EXCEPTIONS,
    LocalStorageCachedDataKeysHMI,
    LocalStorageGlobalKeys,
} from "constants/constants";
import { LocalserverSetupMethodsEntity } from "mqtt/entities/LocalserverSetupMethods/LocalserverSetupMethodsEntity";
import { getLastTwoShiftsEvents } from "api/queries/ShiftsAPI";
import { getHMIData } from "api/queries/hmiDataAPI";
import { API } from "../../api/API";
import { validateLocalserverDataFreshness } from "utilities/utilities";
import { parseIntervalStartMs } from "utilities/qualityIntervals";

export const setCachedData = (setStore: (value: React.SetStateAction<IStore>) => void, error?: ApolloError) => {
    try {
        let cachedStore = {};
        const machineCenterLocalStorage = getLocalStorageItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER);
        const failureTypesLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPES);
        const dataLastUpdatedLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED);
        const eventReportEditingLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDITING);
        const mqttBrokerSettingsLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS);
        const lastTwoShiftEventsLocalStorage = getLocalStorageItem(LocalStorageCachedDataKeysHMI.LAST_TWO_SHIFT_EVENTS);
        const defaultUptimeValuesLocalStorage = localStorage.getItem(
            LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES,
        );
        const eventReportEditDataLocalStorage = localStorage.getItem(
            LocalStorageCachedDataKeysHMI.EVENT_REPORT_EDIT_DATA,
        );
        const editionsPendingDataLocalStorage = getLocalStorageItem(LocalStorageCachedDataKeysHMI.EDITIONS_PENDING);
        const manualQualityPendingLocalStorage = getManualQualityPendingHMIStore();

        if (
            machineCenterLocalStorage ||
            failureTypesLocalStorage ||
            dataLastUpdatedLocalStorage ||
            eventReportEditingLocalStorage ||
            mqttBrokerSettingsLocalStorage ||
            lastTwoShiftEventsLocalStorage ||
            defaultUptimeValuesLocalStorage ||
            eventReportEditDataLocalStorage ||
            editionsPendingDataLocalStorage ||
            manualQualityPendingLocalStorage
        ) {
            if (machineCenterLocalStorage) {
                const machineCenter: IMachineCenter = JSON.parse(machineCenterLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    machineCenter: machineCenter,
                    apolloQueryError: undefined,
                };
            }
            if (failureTypesLocalStorage) {
                const failureTypesArray: IFailureType[] = JSON.parse(failureTypesLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    failureTypes: failureTypesArray,
                    apolloQueryError: undefined,
                };
            }
            if (dataLastUpdatedLocalStorage) {
                const lastUpdated: Date = JSON.parse(dataLastUpdatedLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    lastUpdated: lastUpdated,
                    apolloQueryError: undefined,
                };
            }
            if (eventReportEditingLocalStorage) {
                const eventReportEditing: boolean | undefined = JSON.parse(eventReportEditingLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    isEventReportEditing: eventReportEditing,
                    apolloQueryError: undefined,
                };
            }
            if (eventReportEditDataLocalStorage) {
                const editEventReportData: IDetails | undefined =
                    JSON.parse(eventReportEditDataLocalStorage!) || eventReportData.row;
                cachedStore = {
                    ...cachedStore,
                    editEventReportData: { row: editEventReportData },
                    apolloQueryError: undefined,
                };
            }

            if (mqttBrokerSettingsLocalStorage) {
                const mqttBrokerSettings: IMQTTBrokerSettings[] = JSON.parse(mqttBrokerSettingsLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    mqttBrokerSettings,
                    apolloQueryError: undefined,
                };
            }

            if (defaultUptimeValuesLocalStorage) {
                const defaultUptimeValues: IDefaultUptimeValues = JSON.parse(defaultUptimeValuesLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    defaultUptimeValues,
                    apolloQueryError: undefined,
                };
            }

            if (lastTwoShiftEventsLocalStorage) {
                const lastTwoShiftsEventReports: ILastTwoShiftsEvents = JSON.parse(lastTwoShiftEventsLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    lastTwoShiftsEventReports,
                    apolloQueryError: undefined,
                };
            }

            if (editionsPendingDataLocalStorage) {
                const editionsPending: IEventsEditionsPending = JSON.parse(editionsPendingDataLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    editionsPending,
                    apolloQueryError: undefined,
                };
            }

            if (manualQualityPendingLocalStorage) {
                const manualQualityPending: IManualQualityPending = manualQualityPendingLocalStorage;
                cachedStore = {
                    ...cachedStore,
                    manualQualityPending,
                    apolloQueryError: undefined,
                };
            }
        }
        setStore((prevStore) => {
            return {
                ...prevStore,
                ...cachedStore,
                ...(error && { apolloQueryError: error }),
            };
        });
    } catch (error) {
        throw error;
    }
};

export const setParsedData = (
    machineCenterInfo: IMachineCenter,
    failureTypes: IFailureType[],
    lastTwoShiftsEvents: ILastTwoShiftsEvents,
    noOperatorId: number,
    setStore: (value: React.SetStateAction<IStore>) => void,
    updateDate?: Date,
) => {
    try {
        const machineCenter: IMachineCenter = machineCenterInfo;
        const failureTypesArray: IFailureType[] = failureTypes;
        const currentShiftDowntimeEvent = lastTwoShiftsEvents?.currentShiftEventWithHours || [];
        const previousShiftDowntimeEvent = lastTwoShiftsEvents?.previousShiftEventWithHours || [];
        const eventReportsArray: IEventWithHours[] = [...previousShiftDowntimeEvent, ...currentShiftDowntimeEvent];
        const lastUpdated: Date | undefined = updateDate || new Date();
        const isEventReportEditing: boolean | undefined = getEventReportEditing() ?? false;
        const editEventReportData: IDetails | undefined = getEventReportEditData() ?? eventReportData.row;
        const editionsPendingLocalStorage = getEditionsPendingHMIStore() || {};
        const mqttBrokerSettings = machineCenter?.production_line?.mill?.mqtt_broker_settings;
        const lastTwoShiftsDowntimeEventOnlyReports = lastTwoShiftsEvents
            ? {
                  ...lastTwoShiftsEvents,
                  currentShiftEventWithHours: currentShiftDowntimeEvent,
                  previousShiftEventWithHours: previousShiftDowntimeEvent,
              }
            : undefined;
        const defaultUptimeValues: IDefaultUptimeValues = {
            no_operator_id: noOperatorId,
            not_selected_type_id: failureTypesArray.find(
                (ft: IFailureType) => ft.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
            )?.failure_type_id,
        };

        setStore((prev) => {
            const editionsPendingUpdate: IEventsEditionsPending = machineCenter?.cluster
                ? Object.entries({
                      ...editionsPendingLocalStorage,
                  }).reduce(
                      (
                          acc: IEventsEditionsPending,
                          [id, details]: [id: string, details: IEventsEditionsPending[0]],
                      ) => {
                          if (details.machineCenterId !== machineCenter.machine_center_id) {
                              acc[Number(id)] = details;
                          }
                          return acc;
                      },
                      {},
                  )
                : {};

            for (const event of eventReportsArray) {
                const recenetlyUpdateDate = editionsPendingLocalStorage[event.event_report_id]?.lastUpdateDate;
                if (!!recenetlyUpdateDate) {
                    const isNotUpdated = !event.updated_at || recenetlyUpdateDate >= event.updated_at;
                    if (isNotUpdated)
                        editionsPendingUpdate[event.event_report_id] = {
                            ...editionsPendingLocalStorage[event.event_report_id],
                        };
                }
            }

            setLocalStorageItem(LocalStorageCachedDataKeysHMI.EDITIONS_PENDING, JSON.stringify(editionsPendingUpdate));

            // Manual quality pending: content-based dual-gate clear. A pending
            // entry persists until BOTH (a) the local-server has acked the
            // submission via MQTT (`mqttAcked`) AND (b) a matching raw manual
            // output row actually appears in the synced data. The content check
            // — against the definitive raw output (`currentShiftQualityManualScrap`
            // / `previousShiftQualityManualScrap`), not the lossy calc summary —
            // is what proves the value persisted (DB + local-server cache, or
            // the cloud DB on fallback). Without it, an in-flight stale sync
            // could clear the overlay before the value is observable.
            // `parseIntervalStartMs` normalizes the cloud ("YYYY-MM-DD HH:MM:SS")
            // and local-server (ISO) interval_start formats.
            const manualQualityPendingLocalStorage = prev.manualQualityPending || {};
            const manualQualityPendingUpdate: typeof manualQualityPendingLocalStorage = {};
            const syncedManualRows = [
                ...(lastTwoShiftsEvents?.currentShiftQualityManualScrap ?? []),
                ...(lastTwoShiftsEvents?.previousShiftQualityManualScrap ?? []),
            ];
            for (const [key, entry] of Object.entries(manualQualityPendingLocalStorage)) {
                if (!entry.mqttAcked) {
                    // First gate not met — keep until the local-server confirms.
                    manualQualityPendingUpdate[key] = entry;
                    continue;
                }
                const targetMs = parseIntervalStartMs(entry.hourAlignedIntervalStartIso);
                const observed = syncedManualRows.some(
                    (r) => parseIntervalStartMs(r.interval_start) === targetMs && r.value === entry.value,
                );
                if (observed) {
                    // Both gates met — drop the pending overlay.
                    continue;
                }
                // Acked but not yet observed in the synced data — keep the overlay.
                manualQualityPendingUpdate[key] = entry;
            }
            setLocalStorageItem(
                LocalStorageCachedDataKeysHMI.MANUAL_QUALITY_PENDING,
                JSON.stringify(manualQualityPendingUpdate),
            );

            return {
                ...prev,
                machineCenter: machineCenter,
                failureTypes: failureTypesArray,
                eventReports: eventReportsArray,
                lastUpdated: lastUpdated,
                isEventReportEditing: isEventReportEditing,
                editEventReportData: { row: editEventReportData },
                mqttBrokerSettings,
                lastTwoShiftsEventReports: lastTwoShiftsDowntimeEventOnlyReports,
                defaultUptimeValues,
                editionsPending: editionsPendingUpdate,
                manualQualityPending: manualQualityPendingUpdate,
            };
        });

        setLocalStorageItem(LocalStorageCachedDataKeysHMI.MACHINE_CENTER, JSON.stringify(machineCenter));
        localStorage.setItem(LocalStorageCachedDataKeysHMI.FAILURE_TYPES, JSON.stringify(failureTypesArray));
        localStorage.setItem(LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED, JSON.stringify(lastUpdated));
        localStorage.setItem(LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS, JSON.stringify(mqttBrokerSettings));
        setLocalStorageItem(
            LocalStorageCachedDataKeysHMI.LAST_TWO_SHIFT_EVENTS,
            JSON.stringify(lastTwoShiftsDowntimeEventOnlyReports),
        );
        localStorage.setItem(LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES, JSON.stringify(defaultUptimeValues));
        setEventReportEditData(editEventReportData);
    } catch (error) {
        throw new Error(`Error while setting parsed data: ${error}`);
    }
};

export const getInfoFromCloudserver = async (
    machineCenterId: number,
    setStore: (value: React.SetStateAction<IStore>) => void,
) => {
    try {
        const apolloClient = new API().client;
        const includeOEE = true;

        const [lastTwoShiftsEventsResponse, apolloResponse] = await Promise.all([
            getLastTwoShiftsEvents(machineCenterId, undefined, includeOEE),
            apolloClient.query({
                query: getHMIData,
                variables: {
                    machine_center_id: machineCenterId,
                },
            }),
        ]);

        const lastTwoShiftsEvents = lastTwoShiftsEventsResponse?.data;
        const { error, data } = apolloResponse;

        if (data?.machine_center && !error && lastTwoShiftsEvents) {
            if (Array.isArray(data?.machine_center) && data?.machine_center?.length! === 0) {
                resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);
                window.location.reload();
                return;
            }

            const machineCenter: IMachineCenter = data.machine_center[0];
            const failureTypesArray: IFailureType[] = data.failure_type;
            const noOperatorId: number = data?.no_operator_details[0]?.user_id;

            setParsedData(machineCenter, failureTypesArray, lastTwoShiftsEvents, noOperatorId, setStore);
        } else {
            throw error || new Error(`Error while getting machine center info from cloud server`);
        }
    } catch (error) {
        throw error;
    }
};

export const getInfoFromLocalserver = async (
    machineCenterId: number,
    setStore: (value: React.SetStateAction<IStore>) => void,
    mqttBrokerSettings: IMQTTBrokerSettings,
) => {
    try {
        const response = await LocalserverSetupMethodsEntity.getMachineCenterInfo(machineCenterId, mqttBrokerSettings);

        if (response?.machineCenterInfo) {
            validateLocalserverDataFreshness(response?.dataUpdatedTime);

            const { failure_types, last_two_shifts_info, no_operator_id, ...machineCenterInfo } =
                response.machineCenterInfo;

            setParsedData(
                machineCenterInfo,
                failure_types,
                last_two_shifts_info,
                no_operator_id,
                setStore,
                response?.dataUpdatedTime,
            );
        } else {
            throw new Error(`No machine center info received from localserver: ${response}`);
        }
    } catch (error) {
        throw new Error(`Error while getting machine center info from localserver: ${error}`);
    }
};
